import type { LinkAssignment, LinkSuggestion } from "@brasilnovo/shared";

/** Linha de carga como vem de GET /operacoes?priorizar_vinculos=true. */
export interface Carga {
  id: number;
  empresa: string;
  numero_ordem: string;
  cliente_entrega: string | null;
  cidade_entrega: string | null;
  peso: number | null;
  data_hora_coleta: string | null;
  patio_coleta: string | null;
  unidade_origem: string | null;
  motorista: string | null;
  cavalo: string | null;
  score_vinculo?: number;
  tem_sugestao_positiva?: boolean;
  tipo_veiculo?: string | null;
  status_carga?: string | null;
  frete_especial?: string | null;
  urgente?: boolean | null;
  prioridade?: boolean | null;
  critica?: boolean | null;
  data_hora_solicitada?: string | null;
}

// Peso da situação confirmada dentro de um MESMO score. Um motorista pronto
// (available) vence um "a confirmar", que vence um em conflito. O score mede
// evidência de vínculo com a carga; a disponibilidade desempata — nunca o
// contrário, porque a torre quer ver primeiro quem a fonte já associou à carga.
const FIT_ORDEM: Record<string, number> = { ready: 0, expected: 1, confirm: 2, conflict: 3 };

/** Ordena as sugestões para o painel: score desc, disponibilidade, nome. */
export function ordenarPorScore<T extends Pick<LinkSuggestion, "score" | "motorista" | "availability">>(rows: T[]): T[] {
  return [...rows].sort((a, b) =>
    (b.score - a.score)
    || ((FIT_ORDEM[a.availability?.fit ?? "confirm"] ?? 2) - (FIT_ORDEM[b.availability?.fit ?? "confirm"] ?? 2))
    || a.motorista.localeCompare(b.motorista, "pt-BR"),
  );
}

/** Ordena as cargas: primeiro as com vínculo comprovado (score maior), depois coleta mais próxima. */
export function ordenarCargas(rows: Carga[]): Carga[] {
  return [...rows].sort((a, b) =>
    ((b.score_vinculo ?? 0) - (a.score_vinculo ?? 0))
    || (a.data_hora_coleta ?? "9999").localeCompare(b.data_hora_coleta ?? "9999")
    || a.id - b.id,
  );
}

/**
 * Payload do PATCH em viagem_vega ao soltar um motorista na carga. Placas em
 * branco viram null: gravar "" quebraria a busca por placa (ILIKE '%%' casa tudo)
 * e o formulário de edição mostraria um campo "preenchido" com nada.
 */
export function montarPatch(a: LinkAssignment): Record<string, string | null> {
  const placa = (v: string) => (v?.trim() ? v.trim().toUpperCase().replace(/[^A-Z0-9]/g, "") : null);
  return {
    motorista: a.motorista.trim(),
    cavalo: placa(a.cavalo),
    primeira_carreta: placa(a.primeira_carreta),
    dolly: placa(a.dolly),
    segunda_carreta: placa(a.segunda_carreta),
  };
}

/** Tipo MIME próprio: impede que um arrasto de texto/arquivo qualquer "solte" numa carga. */
export const DND_MIME = "application/x-brasilnovo-motorista";

export function serializarArrasto(s: LinkAssignment & { id: string; score: number }): string {
  return JSON.stringify({ id: s.id, score: s.score, motorista: s.motorista, cavalo: s.cavalo, primeira_carreta: s.primeira_carreta, dolly: s.dolly, segunda_carreta: s.segunda_carreta });
}

export function lerArrasto(raw: string): (LinkAssignment & { id: string; score: number }) | null {
  try {
    const v = JSON.parse(raw);
    if (!v || typeof v.motorista !== "string" || !v.motorista.trim()) return null;
    return { id: String(v.id ?? ""), score: Number(v.score ?? 0), motorista: v.motorista, cavalo: v.cavalo ?? "", primeira_carreta: v.primeira_carreta ?? "", dolly: v.dolly ?? "", segunda_carreta: v.segunda_carreta ?? "" };
  } catch {
    return null;
  }
}

/** Tom visual do score, compartilhado entre cartão de carga e de motorista. */
export function tomScore(score: number): "alto" | "medio" | "nenhum" {
  return score >= 80 ? "alto" : score > 0 ? "medio" : "nenhum";
}
